import React, {useEffect, useMemo, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {toast} from 'react-toastify';
import {Modal} from 'react-bootstrap';
import {Loader} from 'components';
import './_reportDetail.scss';
import {routes} from 'common';
import {api} from 'api/calls';
import {REPORT_STATUS, REPORT_STATUS_BADGE, REPORT_STATUS_LABEL, REPORT_TYPE} from './constants';
import {IoChevronDown, IoChevronUp} from 'react-icons/io5';
import {formatPrice} from 'common/utils';

function ReportDetail() {
  const {id} = useParams();
  const navigate = useNavigate();

  const [fetchLoading, setFetchLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [reportData, setReportData] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [actionType, setActionType] = useState('');
  const [adminNotes, setAdminNotes] = useState('');

  // Expand/Collapse sections
  const [expandedReportInfo, setExpandedReportInfo] = useState(true);
  const [expandedReporter, setExpandedReporter] = useState(true);
  const [expandedRelatedBooking, setExpandedRelatedBooking] = useState(true);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        setFetchLoading(true);
        const data = await api.getReportDetail(id);
        setReportData(data);
      } catch (e) {
        toast.error('Failed to fetch report');
        navigate(routes.REPORTS);
      } finally {
        setFetchLoading(false);
      }
    };

    if (id) {
      fetchReport();
    }
  }, [id, navigate]);

  const canMarkUnderReview = useMemo(() => reportData?.status === REPORT_STATUS.PENDING, [reportData]);
  const canResolveOrDismiss = useMemo(
    () => reportData?.status === REPORT_STATUS.PENDING || reportData?.status === REPORT_STATUS.UNDER_REVIEW,
    [reportData]
  );

  const openActionModal = (type) => {
    setActionType(type);
    setAdminNotes('');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setActionType('');
    setAdminNotes('');
  };

  const handleConfirmAction = async () => {
    if (!reportData) return;

    setActionLoading(true);
    try {
      if (actionType === REPORT_STATUS.UNDER_REVIEW) {
        await api.markReportUnderReview(reportData.id);
        toast.success('Report marked as Under Review');
      } else if (actionType === REPORT_STATUS.RESOLVED) {
        await api.resolveReport(reportData.id, {admin_notes: adminNotes});
        toast.success('Report resolved');
      } else if (actionType === REPORT_STATUS.DISMISSED) {
        await api.dismissReport(reportData.id, {admin_notes: adminNotes});
        toast.success('Report dismissed');
      }

      const refreshed = await api.getReportDetail(id);
      setReportData(refreshed);
      closeModal();
    } catch (e) {
      toast.error('Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadgeClass = (status) => REPORT_STATUS_BADGE[status] || 'badge-secondary';

  const getReportTypeDisplay = (type) => {
    const mapping = {
      [REPORT_TYPE.PAYMENT_ISSUE]: 'Payment Issue',
      [REPORT_TYPE.SERVICE_QUALITY]: 'Service Quality',
      [REPORT_TYPE.BOOKING_CONFLICT]: 'Booking Conflict',
      [REPORT_TYPE.GROUND_CONDITION]: 'Ground Condition',
      [REPORT_TYPE.STAFF_BEHAVIOR]: 'Staff Behavior',
      [REPORT_TYPE.OTHER]: 'Other',
    };
    return mapping[type] || type;
  };

  if (!reportData && !fetchLoading) {
    return (
      <div className="report-detail-page detail-page">
        <div className="error-container">
          <p>Report not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="report-detail-page detail-page">
      {(fetchLoading || actionLoading) && <Loader/>}

      <div className="header">
        <h1>Report Details</h1>
        <p className="subtitle">View and manage booking report</p>

        {reportData && (
          <div className="approval-status-bar">
            <div className="approval-status-section">
              <span className={`approval-status ${reportData.status}`}>
                {REPORT_STATUS_LABEL[reportData.status] || reportData.status}
              </span>
              <div className="status-message">
                {reportData.status === REPORT_STATUS.PENDING && 'This report is awaiting review.'}
                {reportData.status === REPORT_STATUS.UNDER_REVIEW && 'This report is currently under review.'}
                {reportData.status === REPORT_STATUS.RESOLVED && 'This report has been resolved.'}
                {reportData.status === REPORT_STATUS.DISMISSED && 'This report has been dismissed.'}
              </div>
            </div>
            <div className="approval-actions">
              {canMarkUnderReview && (
                <button
                  className="approve-btn"
                  disabled={actionLoading}
                  onClick={() => openActionModal(REPORT_STATUS.UNDER_REVIEW)}
                >
                  Mark Under Review
                </button>
              )}
              {canResolveOrDismiss && (
                <>
                  <button
                    className="approve-btn"
                    disabled={actionLoading}
                    onClick={() => openActionModal(REPORT_STATUS.RESOLVED)}
                  >
                    Resolve
                  </button>
                  <button
                    className="reject-btn"
                    disabled={actionLoading}
                    onClick={() => openActionModal(REPORT_STATUS.DISMISSED)}
                  >
                    Dismiss
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {reportData && (
        <div className="custom-form">
          <div className="section-header">
            <div
              className="section-title-wrapper"
              onClick={() => setExpandedReportInfo(!expandedReportInfo)}
              style={{cursor: 'pointer'}}
            >
              <button
                type="button"
                className="expand-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setExpandedReportInfo(!expandedReportInfo);
                }}
                aria-label={expandedReportInfo ? 'Collapse report information' : 'Expand report information'}
              >
                <span className={`expand-icon ${expandedReportInfo ? 'expanded' : ''}`}>
                  {expandedReportInfo ? <IoChevronUp/> : <IoChevronDown/>}
                </span>
              </button>
              <h2>Report Information</h2>
            </div>
          </div>
          {expandedReportInfo && (
          <div className="basic-info-content">
            <div className="form-row">
              <div className="form-group">
                <label>Report ID</label>
                <div className="form-control-static">{reportData.id}</div>
              </div>
              <div className="form-group">
                <label>Type</label>
                <div className="form-control-static">{getReportTypeDisplay(reportData.report_type)}</div>
              </div>
              <div className="form-group">
                <label>Status</label>
                <div className="status-display">
                  <div className="status-badge">
                    <span className={`badge ${getStatusBadgeClass(reportData.status)}`}>
                      {REPORT_STATUS_LABEL[reportData.status] || reportData.status}
                    </span>
                  </div>
                  <div className="status-hint">
                    {reportData.status === REPORT_STATUS.PENDING && 'Awaiting review. You can mark this report under review or resolve/dismiss it.'}
                    {reportData.status === REPORT_STATUS.UNDER_REVIEW && 'Currently being reviewed. Add notes and resolve or dismiss when ready.'}
                    {reportData.status === REPORT_STATUS.RESOLVED && 'This report has been resolved.'}
                    {reportData.status === REPORT_STATUS.DISMISSED && 'This report has been dismissed.'}
                  </div>
                </div>
              </div>
            </div>

            <div className="form-group">
              <label>Description</label>
              <div className="form-control-static">{reportData.description || 'N/A'}</div>
            </div>

            {reportData.admin_notes && (
              <div className="form-group">
                <label>Admin Notes</label>
                <div className="form-control-static">{reportData.admin_notes}</div>
              </div>
            )}

            <div className="info-section">
              <div className="info-item">
                <span className="info-label">Created:</span>
                <span className="info-value">{new Date(reportData.created).toLocaleDateString()}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Modified:</span>
                <span className="info-value">{new Date(reportData.modified).toLocaleDateString()}</span>
              </div>
              {reportData.resolved_at && (
                <div className="info-item">
                  <span className="info-label">Resolved At:</span>
                  <span className="info-value">{new Date(reportData.resolved_at).toLocaleDateString()}</span>
                </div>
              )}
            </div>
          </div>
          )}

          <div className="section-header">
            <div
              className="section-title-wrapper"
              onClick={() => setExpandedReporter(!expandedReporter)}
              style={{cursor: 'pointer'}}
            >
              <button
                type="button"
                className="expand-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setExpandedReporter(!expandedReporter);
                }}
                aria-label={expandedReporter ? 'Collapse reporter' : 'Expand reporter'}
              >
                <span className={`expand-icon ${expandedReporter ? 'expanded' : ''}`}>
                  {expandedReporter ? <IoChevronUp/> : <IoChevronDown/>}
                </span>
              </button>
              <h2>Reporter</h2>
            </div>
          </div>
          {expandedReporter && (
          <div className="basic-info-content">
            <div className="form-row">
              <div className="form-group">
                <label>Full Name</label>
                <div className="form-control-static">{reportData.reported_by?.full_name || 'N/A'}</div>
              </div>
              <div className="form-group">
                <label>Email</label>
                <div className="form-control-static">{reportData.reported_by?.email || 'N/A'}</div>
              </div>
            </div>
          </div>
          )}

          <div className="section-header">
            <div
              className="section-title-wrapper"
              onClick={() => setExpandedRelatedBooking(!expandedRelatedBooking)}
              style={{cursor: 'pointer'}}
            >
              <button
                type="button"
                className="expand-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setExpandedRelatedBooking(!expandedRelatedBooking);
                }}
                aria-label={expandedRelatedBooking ? 'Collapse related booking' : 'Expand related booking'}
              >
                <span className={`expand-icon ${expandedRelatedBooking ? 'expanded' : ''}`}>
                  {expandedRelatedBooking ? <IoChevronUp/> : <IoChevronDown/>}
                </span>
              </button>
              <h2>Related Booking</h2>
            </div>
          </div>
          {expandedRelatedBooking && (
          <div className="basic-info-content">
            <div className="form-row">
              <div className="form-group">
                <label>Reference Number</label>
                <div className="form-control-static">{reportData.booking?.reference_number || 'N/A'}</div>
              </div>
              <div className="form-group">
                <label>Booking ID</label>
                <div className="form-control-static">{reportData.booking?.id || 'N/A'}</div>
              </div>
              <div className="form-group">
                <label>Ground</label>
                <div className="form-control-static">{reportData.booking?.ground_field?.ground?.name || 'N/A'}</div>
              </div>
              <div className="form-group">
                <label>Field</label>
                <div className="form-control-static">{reportData.booking?.ground_field?.name || 'N/A'}</div>
              </div>
              <div className="form-group">
                <label>Date</label>
                <div className="form-control-static">
                  {reportData.booking?.date ? new Date(reportData.booking.date).toLocaleDateString() : 'N/A'}
                </div>
              </div>
              <div className="form-group">
                <label>Start Time</label>
                <div className="form-control-static">{reportData.booking?.start_time || 'N/A'}</div>
              </div>
              <div className="form-group">
                <label>End Time</label>
                <div className="form-control-static">{reportData.booking?.end_time || 'N/A'}</div>
              </div>
              <div className="form-group">
                <label>Price (PKR)</label>
                <div className="form-control-static">Rs. {formatPrice(reportData.booking?.price ?? 0)}</div>
              </div>
            </div>
          </div>
          )}
        </div>
      )}

      <Modal show={showModal} onHide={closeModal} backdrop="static" keyboard={false}>
        <Modal.Header closeButton>
          <Modal.Title>
            {actionType === REPORT_STATUS.RESOLVED
              ? 'Resolve Report'
              : actionType === REPORT_STATUS.DISMISSED
                ? 'Dismiss Report'
                : 'Mark Under Review'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to {actionType === REPORT_STATUS.RESOLVED
            ? 'resolve'
            : actionType === REPORT_STATUS.DISMISSED
              ? 'dismiss'
              : 'mark under review'} this report?
          </p>

          {(actionType === REPORT_STATUS.RESOLVED || actionType === REPORT_STATUS.DISMISSED) && (
            <div className="form-group" style={{marginTop: 12}}>
              <label htmlFor="adminNotes">Admin Notes <span className="optional">(optional)</span></label>
              <div className="notes-wrapper">
                <textarea
                  id="adminNotes"
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  rows={5}
                  placeholder="Add context about what you reviewed or why you resolved/dismissed this report."
                  className="notes-textarea"
                />
                <div className="notes-footer">
                  <span className="notes-hint">This will be stored with the report for audit trail.</span>
                  <span className="notes-count">{adminNotes.length}/500</span>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button className="primary-btn" onClick={closeModal} disabled={actionLoading}>
            Cancel
          </button>
          <button className="primary-btn" onClick={handleConfirmAction} disabled={actionLoading}>
            {actionType === REPORT_STATUS.RESOLVED
              ? 'Resolve'
              : actionType === REPORT_STATUS.DISMISSED
                ? 'Dismiss'
                : 'Mark Under Review'}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default ReportDetail;


